import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RUN_META, checkpointSchedule } from "@/lib/format";

const todayET = (now) => {
  const p = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
  const g = (t) => p.find((x) => x.type === t)?.value;
  return `${g("year")}-${g("month")}-${g("day")}`;
};
const minutesET = (now) => {
  const p = new Intl.DateTimeFormat("en-GB", { timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(now);
  return Number(p.find((x) => x.type === "hour")?.value) * 60 + Number(p.find((x) => x.type === "minute")?.value);
};

/** Aggregate data provenance for the session, in the design system's vocabulary. */
export function sessionProvenance(session, health) {
  if (!session) return "UNAVAILABLE";
  if (session.provider === "polygon") return "LIVE";
  if (health?.stubChain || session.fixture || session.scenario || !session.provider) return "STUB";
  return "LIVE";
}

/**
 * Shared session state for the shell and screens: the selected session, its sealed checkpoints,
 * the current run, the next scheduled run and the six-square indicator.
 */
export function useSessionState(now = new Date()) {
  const { selectedDate, health } = useAuth();
  const { data: session } = useQuery({
    queryKey: ["session", selectedDate],
    queryFn: () => api.getSession(selectedDate),
    enabled: !!selectedDate,
    refetchInterval: 4000,
  });
  const { data: checkpoints = [] } = useQuery({
    queryKey: ["checkpoints", selectedDate],
    queryFn: () => api.getCheckpoints(selectedDate),
    enabled: !!selectedDate,
    refetchInterval: 30000,
  });

  const originals = checkpoints.filter((c) => !c.recalc_of);
  const recalcRuns = new Set(checkpoints.filter((c) => c.recalc_of).map((c) => c.run));
  const doneRuns = new Set(originals.map((c) => c.run));
  const currentRun = originals.reduce((m, c) => Math.max(m, c.run || 0), 0);
  const current = originals.find((c) => c.run === currentRun) || null;

  const isToday = selectedDate === todayET(now);
  const next = isToday
    ? [1, 2, 3, 4, 5, 6]
        .map((run) => ({ run, ...checkpointSchedule(selectedDate, RUN_META[run].time) }))
        .find((s) => s.at && s.at > now.getTime() && !doneRuns.has(s.run))
    : null;

  const mini = [1, 2, 3, 4, 5, 6].map((run) => ({
    id: "R" + run,
    s: recalcRuns.has(run) ? "recalc" : doneRuns.has(run) ? "done" : next && next.run === run ? "current" : "pending",
  }));

  let marketState = "Closed";
  if (session?.status === "holiday") marketState = "Holiday";
  else if (session?.status === "skipped") marketState = "Skipped";
  else if (isToday) {
    const m = minutesET(now);
    marketState = m < 570 ? "Pre-market" : m < 960 ? "Open" : "Closed";
  } else if (selectedDate && selectedDate > todayET(now)) marketState = "Scheduled";

  return {
    selectedDate, session, checkpoints, originals, current, currentRun, doneRuns, next, mini, isToday, marketState,
    provenance: sessionProvenance(session, health),
    killArmed: !!session?.kill_switch,
  };
}
